// Pure routing decisions for the canvas pointer pipeline.
//
// Extracted from main.ts so the wiring seam that every tool depends on is
// actually testable (the app audit found NO test exercised the main.ts pointer
// dispatch, which is how two real bugs shipped: right/middle-click started tool
// operations, and the Ctrl-temp-Move gesture dispatched pointerdown to the
// PREVIOUS tool instead of the Move tool it had just switched to).
import type { Tool } from "../core/tools/Tool";

export interface PointerRoutingTools {
  moveTool: Tool;
  zoomTool: Tool;
}

export interface PointerDownRoute {
  /** True when the event must NOT start a tool operation (non-primary button). */
  ignore: boolean;
  /** True when this pointerdown should engage the Ctrl-temp-Move gesture (#217). */
  engageCtrlMove: boolean;
  /** The tool that must receive onPointerDown for THIS event (post-swap). */
  dispatchTool: Tool;
}

/**
 * Decide how a canvas pointerdown routes.
 *
 * - Only the primary button (0) starts a tool operation; right/middle buttons
 *   are ignored here (the context-menu / brush-HUD path handles right-click).
 * - Ctrl held (without already being in temp-move, and not already on Move/Zoom)
 *   engages a temporary Move tool, and — critically — the pointerdown for that
 *   same event must be dispatched to the Move tool, not the tool that was active
 *   a line earlier.
 */
export function routePointerDown(
  e: { button: number; ctrlKey: boolean },
  activeTool: Tool | null,
  tools: PointerRoutingTools,
  ctrlMoveActive: boolean,
): PointerDownRoute {
  if (!activeTool) {
    return { ignore: true, engageCtrlMove: false, dispatchTool: tools.moveTool };
  }
  if (e.button !== 0) {
    return { ignore: true, engageCtrlMove: false, dispatchTool: activeTool };
  }
  const engageCtrlMove =
    e.ctrlKey && !ctrlMoveActive && activeTool !== tools.moveTool && activeTool !== tools.zoomTool;
  const dispatchTool = engageCtrlMove || ctrlMoveActive ? tools.moveTool : activeTool;
  return { ignore: false, engageCtrlMove, dispatchTool };
}

/**
 * Whether a pointermove with no button held should still be forwarded to the
 * active tool. Most tools only track during a drag (button held), but some are
 * hover-driven (Pen rubber-band, Magnetic/Polygonal Lasso live segment) and need
 * moves even when `dragging` is false.
 */
export function wantsHoverMove(activeTool: Tool | null): boolean {
  return !!activeTool && (activeTool as { tracksHover?: boolean }).tracksHover === true;
}
